import React, { useMemo, useState, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Tune as TuneIcon,
  InfoOutlined as InfoOutlinedIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
} from '@mui/icons-material';
import { useValuations } from '../../../hooks/administration/useValuations';
import AdjustmentForm from './AdjustmentForm';
import formatDate from './utils/formatDate';

const TYPE_CONFIG = {
  AUMENTO: {
    label: 'AUMENTO',
    bg: '#2e7d32',
    color: '#fff',
    icon: <TrendingUpIcon fontSize="small" />,
  },
  DESCUENTO: {
    label: 'DESCUENTO',
    bg: '#d32f2f',
    color: '#fff',
    icon: <TrendingDownIcon fontSize="small" />,
  },
};

const formatCurrency = (value, symbol = 'S/') => {
  const number = Number(value || 0);
  return `${symbol} ${number.toFixed(2)}`;
};

const formatNumber = (value) => {
  const number = Number(value || 0);
  return number.toFixed(2);
};

const normalizeAdjustmentPayload = (payload) => ({
  valajudes: String(payload.valajudes || '').trim(),
  valajucan: Number(payload.valajucan || 0),
  valajumonuni: Number(payload.valajumonuni || 0),
  valajutip: String(payload.valajutip || '').trim().toUpperCase(),
  valajufec: payload.valajufec || null,
  valajufecfin: payload.valajufecfin || null,
});

const formatPeriod = (start, end) => {
  if (!start && !end) return '-';

  const from = formatDate(start);
  const to = formatDate(end || start);

  return from === to ? from : `${from} - ${to}`;
};

const ValuationAdjustmentsSection = ({
  valuation,
  adjustments = [],
  editable = false,
}) => {
  const {
    addAdjustmentMutation,
    updateAdjustmentMutation,
    deleteAdjustmentMutation,
  } = useValuations();

  const [openForm, setOpenForm] = useState(false);
  const [editAdjustment, setEditAdjustment] = useState(null);
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [selectedAdjustment, setSelectedAdjustment] = useState(null);

  const [alert, setAlert] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const summary = useMemo(() => {
    const increases = adjustments
      .filter((item) => item.valajutip === 'AUMENTO')
      .reduce((sum, item) => sum + Number(item.valajumon || 0), 0);

    const discounts = adjustments
      .filter((item) => item.valajutip === 'DESCUENTO')
      .reduce((sum, item) => sum + Number(item.valajumon || 0), 0);

    return {
      total: adjustments.length,
      increases,
      discounts,
      net: increases - discounts,
    };
  }, [adjustments]);

  const showAlert = useCallback((message, severity = 'success') => {
    setAlert({
      open: true,
      message,
      severity,
    });
  }, []);

  const handleCloseAlert = () => {
    setAlert((prev) => ({ ...prev, open: false }));
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    setEditAdjustment(null);
  };

  const handleCreate = (payload) => {
    if (!valuation?.valcabid) return;

    const data = normalizeAdjustmentPayload(payload);

    addAdjustmentMutation.mutate(
      {
        id: valuation.valcabid,
        data,
      },
      {
        onSuccess: () => {
          showAlert('Ajuste registrado correctamente.');
          handleCloseForm();
        },
        onError: (error) => {
          showAlert(error?.message || 'Error al registrar ajuste.', 'error');
        },
      }
    );
  };

  const handleUpdate = (payload) => {
    if (!valuation?.valcabid || !editAdjustment?.valajuid) return;

    const data = normalizeAdjustmentPayload(payload);

    updateAdjustmentMutation.mutate(
      {
        id: valuation.valcabid,
        adjustmentId: editAdjustment.valajuid,
        data,
      },
      {
        onSuccess: () => {
          showAlert('Ajuste actualizado correctamente.');
          handleCloseForm();
        },
        onError: (error) => {
          showAlert(error?.message || 'Error al actualizar ajuste.', 'error');
        },
      }
    );
  };

  const handleDelete = () => {
    if (!valuation?.valcabid || !selectedAdjustment?.valajuid) return;

    deleteAdjustmentMutation.mutate(
      {
        id: valuation.valcabid,
        adjustmentId: selectedAdjustment.valajuid,
      },
      {
        onSuccess: () => {
          showAlert('Ajuste eliminado correctamente.');
          setOpenConfirmDelete(false);
          setSelectedAdjustment(null);
        },
        onError: (error) => {
          showAlert(error?.message || 'Error al eliminar ajuste.', 'error');
        },
      }
    );
  };

  const loadingForm =
    addAdjustmentMutation.isPending || updateAdjustmentMutation.isPending;

  return (
    <>
      <Snackbar
        open={alert.open}
        autoHideDuration={5000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert severity={alert.severity} onClose={handleCloseAlert}>
          {alert.message}
        </Alert>
      </Snackbar>

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          flexDirection={{ xs: 'column', md: 'row' }}
          gap={2}
          mb={2.5}
        >
          <Box>
            <Typography variant="h6" fontWeight="bold">
              Ajustes de la valorización
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Usa ajustes para incrementar o reducir el subtotal antes del cálculo final.
            </Typography>
          </Box>

          {editable && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditAdjustment(null);
                setOpenForm(true);
              }}
              sx={{ textTransform: 'none' }}
            >
              Nuevo ajuste
            </Button>
          )}
        </Box>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mb={2.5}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5, flex: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Total de ajustes
            </Typography>
            <Typography variant="h5" fontWeight="bold" mt={1}>
              {summary.total}
            </Typography>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5, flex: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Aumentos acumulados
            </Typography>
            <Typography variant="h6" fontWeight="bold" mt={1} color="success.main">
              {formatCurrency(summary.increases, valuation?.monsim || 'S/')}
            </Typography>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5, flex: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Descuentos acumulados
            </Typography>
            <Typography variant="h6" fontWeight="bold" mt={1} color="error.main">
              {formatCurrency(summary.discounts, valuation?.monsim || 'S/')}
            </Typography>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5, flex: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Impacto neto
            </Typography>
            <Typography
              variant="h6"
              fontWeight="bold"
              mt={1}
              color={summary.net >= 0 ? 'success.main' : 'error.main'}
            >
              {formatCurrency(summary.net, valuation?.monsim || 'S/')}
            </Typography>
          </Paper>
        </Stack>

        {!editable && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Esta valorización no es editable en su estado actual. Solo puedes visualizar los ajustes registrados.
          </Alert>
        )}

        {adjustments.length === 0 ? (
          <Alert severity="warning" variant="outlined">
            No hay ajustes registrados en esta valorización.
          </Alert>
        ) : (
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{ border: '1px solid #e0e0e0', borderRadius: 2.5 }}
          >
            <Table>
              <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                <TableRow>
                  <TableCell><strong>Tipo</strong></TableCell>
                  <TableCell><strong>Periodo</strong></TableCell>
                  <TableCell><strong>Descripción</strong></TableCell>
                  <TableCell align="right"><strong>Cantidad</strong></TableCell>
                  <TableCell align="right"><strong>Valor unitario</strong></TableCell>
                  <TableCell align="right"><strong>Monto total</strong></TableCell>
                  <TableCell><strong>Impacto</strong></TableCell>
                  <TableCell align="center"><strong>Acciones</strong></TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {adjustments.map((adjustment) => {
                  const type = TYPE_CONFIG[adjustment.valajutip] || {
                    label: adjustment.valajutip,
                    bg: '#bdbdbd',
                    color: '#000',
                    icon: <TuneIcon fontSize="small" />,
                  };

                  return (
                    <TableRow key={adjustment.valajuid} hover>
                      <TableCell>
                        <Chip
                          icon={type.icon}
                          label={type.label}
                          sx={{
                            backgroundColor: type.bg,
                            color: type.color,
                            fontWeight: 'bold',
                          }}
                        />
                      </TableCell>

                      <TableCell>
                        <Chip
                          size="small"
                          variant="outlined"
                          color="info"
                          label={formatPeriod(adjustment.valajufec, adjustment.valajufecfin)}
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>

                      <TableCell sx={{ maxWidth: 360 }}>
                        <Typography>
                          {adjustment.valajudes}
                        </Typography>
                      </TableCell>

                      <TableCell align="right">
                        <Typography fontWeight="medium">
                          {formatNumber(adjustment.valajucan)}
                        </Typography>
                      </TableCell>

                      <TableCell align="right">
                        <Typography fontWeight="medium">
                          {formatCurrency(adjustment.valajumonuni, valuation?.monsim || 'S/')}
                        </Typography>
                      </TableCell>

                      <TableCell align="right">
                        <Typography fontWeight="bold">
                          {formatCurrency(adjustment.valajumon, valuation?.monsim || 'S/')}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography
                          fontWeight="medium"
                          color={
                            adjustment.valajutip === 'DESCUENTO'
                              ? 'error.main'
                              : 'success.main'
                          }
                        >
                          {adjustment.valajutip === 'DESCUENTO'
                            ? 'Reduce subtotal'
                            : 'Incrementa subtotal'}
                        </Typography>
                      </TableCell>

                      <TableCell align="center">
                        <Box display="flex" justifyContent="center" gap={0.5}>
                          {editable && (
                            <>
                              <Tooltip title="Editar ajuste">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setEditAdjustment(adjustment);
                                    setOpenForm(true);
                                  }}
                                >
                                  <EditIcon color="primary" fontSize="small" />
                                </IconButton>
                              </Tooltip>

                              <Tooltip title="Eliminar ajuste">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setSelectedAdjustment(adjustment);
                                    setOpenConfirmDelete(true);
                                  }}
                                >
                                  <DeleteIcon color="error" fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Box mt={2.5}>
          <Alert severity="info" icon={<InfoOutlinedIcon />}>
            El monto total del ajuste se calcula automáticamente como cantidad por valor unitario.
          </Alert>
        </Box>
      </Paper>

      <AdjustmentForm
        open={openForm}
        handleClose={handleCloseForm}
        defaultValues={editAdjustment || {}}
        onSubmit={editAdjustment ? handleUpdate : handleCreate}
        loading={loadingForm}
        currencySymbol={valuation?.monsim || 'S/'}
      />

      <Dialog
        open={openConfirmDelete}
        onClose={() => setOpenConfirmDelete(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirmar eliminación de ajuste</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Desea eliminar este ajuste de la valorización?
          </DialogContentText>

          {selectedAdjustment && (
            <Box mt={2}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Tipo
                </Typography>
                <Typography fontWeight="bold" mb={1}>
                  {selectedAdjustment.valajutip}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  Descripción
                </Typography>
                <Typography mb={1}>
                  {selectedAdjustment.valajudes}
                </Typography>

                <Stack direction="row" spacing={2} mb={1}>
                  <Box flex={1}>
                    <Typography variant="body2" color="text.secondary">
                      Cantidad
                    </Typography>
                    <Typography fontWeight="bold">
                      {formatNumber(selectedAdjustment.valajucan)}
                    </Typography>
                  </Box>

                  <Box flex={1}>
                    <Typography variant="body2" color="text.secondary">
                      Valor unitario
                    </Typography>
                    <Typography fontWeight="bold">
                      {formatCurrency(selectedAdjustment.valajumonuni, valuation?.monsim || 'S/')}
                    </Typography>
                  </Box>
                </Stack>

                <Typography variant="body2" color="text.secondary">
                  Monto total
                </Typography>
                <Typography fontWeight="bold">
                  {formatCurrency(selectedAdjustment.valajumon, valuation?.monsim || 'S/')}
                </Typography>
              </Paper>
            </Box>
          )}

          <Box mt={2}>
            <Alert severity="warning">
              Esta acción recalculará automáticamente los totales de la valorización.
            </Alert>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenConfirmDelete(false)}>
            Cancelar
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={deleteAdjustmentMutation.isPending}
          >
            {deleteAdjustmentMutation.isPending ? 'Eliminando...' : 'Eliminar ajuste'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ValuationAdjustmentsSection;